import { z } from 'zod';
import { APP_SECTION_IDS } from './app-sections';
import { DailyEnergyQuotaDto } from './energy-quotas';
import { EventListItemDto } from './event';
import { PublicProfileDto } from './networking';

/** Потолок шагов цикла агента на одну реплику пользователя. */
export const ASSISTANT_AGENT_MAX_STEPS = 4;
/** Сколько последних сообщений треда уезжает в модель. */
export const ASSISTANT_HISTORY_WINDOW = 12;
/** Бесплатных уточняющих вопросов в сутки (UTC), далее уточнение платное. */
export const ASSISTANT_FREE_CLARIFY_PER_DAY = 3;
/** Карточек одного домена в снимке одного сообщения. */
export const ASSISTANT_CARDS_PER_DOMAIN = 3;
/** Срок жизни предложенного действия. */
export const ASSISTANT_PENDING_ACTION_TTL_HOURS = 24;

/**
 * Потолок длины одной реплики человека.
 *
 * Было 500 — хватало, пока у ассистента спрашивали «найди ивент на вечер».
 * С приходом заполнения профиля жанр сменился: чтобы ассистент собрал анкету
 * и визитку, человек обязан надиктовать рассказ о себе, и 500 символов режут
 * его на полуслове (стейдж 2026-09-16: реплика на ~600 символов четырежды
 * получила 400 ещё до всякой логики, а экран предлагал бесполезный повтор).
 * В модель уезжает окно из ASSISTANT_HISTORY_WINDOW последних сообщений,
 * поэтому потолок — это ещё и прямой множитель счёта за реплику.
 */
export const ASSISTANT_MESSAGE_MAX_CHARS = 2000;

export const AskAssistantInput = z.object({
  message: z.string().trim().min(1).max(ASSISTANT_MESSAGE_MAX_CHARS),
});
export type AskAssistantInput = z.infer<typeof AskAssistantInput>;

export const SendAssistantMessageInput = z.object({
  message: z.string().trim().min(1).max(ASSISTANT_MESSAGE_MAX_CHARS),
  /** Идентификатор реплики от клиента: ключ дедупликации и requestId квоты. */
  clientMessageId: z.uuid(),
  /**
   * Реплика — нажатие чипа, который ассистент показал сам. В тред она ложится
   * обычным текстом (человек видит именно её), но поисковой строкой быть не
   * может: подпись нашей же кнопки («Люди для общения») в полнотекстовом
   * поиске даёт гарантированный ноль. Необязательное — старые бандлы в
   * вебвью живых юзеров его не знают.
   */
  fromChip: z.boolean().optional(),
  /** First introduction from People: save the user's wishes through existing confirmed actions. */
  peopleIntro: z.boolean().optional(),
  /** Код визитки, с кнопки которой открыт разговор. Пишется только при создании треда. */
  cardCode: z
    .string()
    .regex(/^[a-z0-9]{4,16}$/)
    .optional(),
  cardIntent: z.enum(['str', 'cmp', 'syn', 'tog', 'ai']).optional(),
});
export type SendAssistantMessageInput = z.infer<typeof SendAssistantMessageInput>;

export const PeopleGoal = z.enum([
  'sport',
  'talk',
  'business',
  'consult',
  'food',
  'walk',
  'romance',
  'vibe',
  'comfort',
  'growth',
  'unclear',
]);
export type PeopleGoal = z.infer<typeof PeopleGoal>;

export const EventsGoal = z.enum(['sport', 'development', 'social', 'creativity', 'food', 'unclear']);
export type EventsGoal = z.infer<typeof EventsGoal>;

export const AssistantCardDomain = z.enum(['event', 'person', 'service', 'match']);
export type AssistantCardDomain = z.infer<typeof AssistantCardDomain>;

/** Ссылка на карточку, которую сервер сам показал в треде: `<домен>:<uuid>`. */
export const AssistantCardRef = z
  .string()
  .regex(
    /^(event|person|service|match):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
    'card ref must be "<domain>:<uuid>"',
  );
export type AssistantCardRef = z.infer<typeof AssistantCardRef>;

export const AssistantToolName = z.enum([
  'search_events',
  'search_people',
  'search_people_by_interest',
  'search_partners',
  'search_partner_offers',
  'search_services',
  'list_my_matches',
  'list_my_upcoming',
  'read_profile',
  'read_availability',
  'list_auto_meetings',
  'read_meeting_prep',
  'rsvp_event',
  'create_meeting',
  'create_service',
  'create_event',
  'create_partner_offer',
  'contact_service',
  'update_profile',
  'update_networking_profile',
  'update_public_card',
  'update_profile_facet',
  'update_city',
  'update_availability',
  'respond_auto_meeting',
  'note_meeting_prep',
]);
export type AssistantToolName = z.infer<typeof AssistantToolName>;

/**
 * Состояния предложенного действия. Вынесено из AssistantActionResultDto:
 * тот же набор нужен и карточке в истории треда, а prisma-enum фронту
 * импортировать нельзя.
 */
export const AssistantActionStatus = z.enum([
  'PENDING',
  'EXECUTING',
  'CONFIRMED',
  'DECLINED',
  'FAILED',
  'EXPIRED',
]);
export type AssistantActionStatus = z.infer<typeof AssistantActionStatus>;

export const AssistantActionDto = z.object({
  id: z.uuid(),
  tool: AssistantToolName,
  title: z.string(),
  /** Человекочитаемые детали, собранные из БД на момент предложения. */
  details: z.array(z.string()),
  /** Цена подтверждения в энергии; 0 — бесплатно. */
  energyCost: z.number().int().min(0),
  expiresAt: z.iso.datetime(),
  /** Живое состояние на момент чтения треда, а не на момент предложения. */
  status: AssistantActionStatus,
  failureCode: z.string().nullable(),
  /** Исход подтверждения: ссылки на созданные сущности. */
  resultRef: z.record(z.string(), z.unknown()).nullable(),
});
export type AssistantActionDto = z.infer<typeof AssistantActionDto>;

export const AssistantMatchDto = z.object({
  shared: z.string(),
  influence: z.string(),
  together: z.string(),
  why: z.array(z.string()).min(1).max(3),
});
export type AssistantMatchDto = z.infer<typeof AssistantMatchDto>;

export const AssistantPersonDto = PublicProfileDto.extend({
  match: AssistantMatchDto.optional(),
});
export type AssistantPersonDto = z.infer<typeof AssistantPersonDto>;

/** Цена одного платного перебора человека недели. */
export const ASSISTANT_PERSON_REROLL_COST = 10;
/** Сколько раз за неделю можно перебрать человека за деньги. */
export const ASSISTANT_PERSON_REROLLS_PER_WEEK = 3;

export const AssistantPersonOfWeekDto = z.object({
  /** null — в городе некого показать (пустая колода). */
  person: AssistantPersonDto.nullable(),
  rerollsLeft: z.number().int().min(0),
  /**
   * Цена перебора приходит с сервера, а не берётся клиентом из константы:
   * цена — серверное правило, и клиент не должен печатать её из своей копии.
   */
  rerollCost: z.number().int().min(0),
  /** Когда сменится сам: следующий понедельник 00:00 UTC. */
  nextPersonAt: z.iso.datetime(),
});
export type AssistantPersonOfWeekDto = z.infer<typeof AssistantPersonOfWeekDto>;

export const AssistantEventMatchDto = z.object({
  why: z.string(),
  fit: z.string(),
  facts: z.array(z.string()).min(1).max(3),
});
export type AssistantEventMatchDto = z.infer<typeof AssistantEventMatchDto>;

export const AssistantEventDto = EventListItemDto.extend({
  match: AssistantEventMatchDto.optional(),
});
export type AssistantEventDto = z.infer<typeof AssistantEventDto>;

export const AssistantServiceCardDto = z.object({
  id: z.uuid(),
  title: z.string(),
  priceText: z.string().nullable().default(null),
  /**
   * Услуги внешних исполнителей (`providerUserId` пуст) показываем, но
   * действие для них недоступно: ServicesService.contact их отвергает.
   */
  canContact: z.boolean(),
});
export type AssistantServiceCardDto = z.infer<typeof AssistantServiceCardDto>;

export const AssistantMatchCardDto = z.object({
  userId: z.uuid(),
  displayName: z.string(),
});
export type AssistantMatchCardDto = z.infer<typeof AssistantMatchCardDto>;

/** Куда может вести навигационный чип — id из реестра APP_SECTIONS. Свободный URL моделью не задаётся. */
export const AssistantNavTarget = z.enum(APP_SECTION_IDS);
export type AssistantNavTarget = z.infer<typeof AssistantNavTarget>;

/** Потолок чипов в одном ответе и длины подписи. */
export const ASSISTANT_CHIPS_MAX = 8;
export const ASSISTANT_CHIP_LABEL_MAX = 40;

const chipLabel = z.string().trim().min(1).max(ASSISTANT_CHIP_LABEL_MAX);

/**
 * Кнопка под ответом: либо реплика в чат, либо переход по allowlist.
 * Строка — легаси из сохранённых тредов (`chips: ['сегодня']`).
 */
export const AssistantChipDto = z.union([
  z.object({ kind: z.literal('nav'), label: chipLabel, to: AssistantNavTarget }),
  z.object({ kind: z.literal('reply'), label: chipLabel }),
  chipLabel.transform((label) => ({ kind: 'reply' as const, label })),
]);
export type AssistantChipDto = z.infer<typeof AssistantChipDto>;

const assistantReplyBase = {
  reply: z.string(),
  quota: DailyEnergyQuotaDto,
  chips: z.array(AssistantChipDto).max(ASSISTANT_CHIPS_MAX).default([]),
};

export const AssistantReplyDto = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('events'), ...assistantReplyBase, events: z.array(AssistantEventDto) }),
  z.object({ kind: z.literal('people'), ...assistantReplyBase, people: z.array(AssistantPersonDto) }),
  /**
   * Деловой поиск. Отдельный вид, а не признак внутри `people`: накопитель
   * карточек хода (`CardBucket`) ключуется видом ответа, и при общем виде
   * человек недели и найденные партнёры слились бы в один список. Плюс
   * недельные элементы карточки (смена по понедельникам, платный перебор) к
   * деловой выдаче отношения не имеют.
   */
  z.object({ kind: z.literal('partners'), ...assistantReplyBase, people: z.array(AssistantPersonDto) }),
  /**
   * Люди, чьи занятия близки тому, чем человек хочет заняться. Третий вид с
   * карточками людей — по той же причине, по которой деловой отделён от
   * человека недели: накопитель карточек хода ключуется видом, и поиск
   * «с кем побегать» слился бы в один список с деловым поиском, хотя
   * объяснения у них написаны в разных рамках.
   */
  z.object({ kind: z.literal('companions'), ...assistantReplyBase, people: z.array(AssistantPersonDto) }),
  z.object({
    kind: z.literal('clarify'),
    ...assistantReplyBase,
  }),
  z.object({ kind: z.literal('none'), ...assistantReplyBase }),
  z.object({ kind: z.literal('action'), ...assistantReplyBase, action: AssistantActionDto }),
  z.object({
    kind: z.literal('services'),
    ...assistantReplyBase,
    services: z.array(AssistantServiceCardDto),
  }),
  z.object({ kind: z.literal('matches'), ...assistantReplyBase, matches: z.array(AssistantMatchCardDto) }),
]);
export type AssistantReplyDto = z.infer<typeof AssistantReplyDto>;

export const AssistantThreadListItemDto = z.object({
  id: z.uuid(),
  title: z.string(),
  lastMessageAt: z.iso.datetime(),
});
export type AssistantThreadListItemDto = z.infer<typeof AssistantThreadListItemDto>;

export const AssistantMessageDto = z.object({
  id: z.uuid(),
  role: z.enum(['USER', 'ASSISTANT']),
  text: z.string(),
  createdAt: z.iso.datetime(),
  /** Снимок карточек ответа. У реплик пользователя — null. */
  reply: AssistantReplyDto.nullable(),
  /** Ключ дедупликации реплики пользователя; у ответов ассистента пуст. */
  clientMessageId: z.string().nullable(),
});
export type AssistantMessageDto = z.infer<typeof AssistantMessageDto>;

export const AssistantThreadDto = z.object({
  id: z.uuid(),
  title: z.string(),
  messages: z.array(AssistantMessageDto),
});
export type AssistantThreadDto = z.infer<typeof AssistantThreadDto>;

/** Потолок и дефолт лимита страницы списка тредов. */
export const ASSISTANT_THREAD_LIST_LIMIT_DEFAULT = 20;
export const ASSISTANT_THREAD_LIST_LIMIT_MAX = 100;

export const AssistantThreadListQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(ASSISTANT_THREAD_LIST_LIMIT_MAX)
    .default(ASSISTANT_THREAD_LIST_LIMIT_DEFAULT),
});
export type AssistantThreadListQuery = z.infer<typeof AssistantThreadListQuery>;

export const AssistantThreadListResponse = z.object({
  items: z.array(AssistantThreadListItemDto),
  nextCursor: z.string().nullable(),
});
export type AssistantThreadListResponse = z.infer<typeof AssistantThreadListResponse>;

/** Ответ на создание треда: новый id + ответ цикла на первую реплику. */
export const CreateAssistantThreadResponse = z.object({
  threadId: z.uuid(),
  reply: AssistantReplyDto,
});
export type CreateAssistantThreadResponse = z.infer<typeof CreateAssistantThreadResponse>;

export const AssistantActionResultDto = z.object({
  id: z.uuid(),
  tool: AssistantToolName,
  status: AssistantActionStatus,
  /** Машиночитаемый код отказа: insufficient_energy, event_full, gone, expired, failed. */
  failureCode: z.string().nullable(),
  resultRef: z.record(z.string(), z.unknown()).nullable(),
});
export type AssistantActionResultDto = z.infer<typeof AssistantActionResultDto>;
